import React, { useMemo } from 'react'
import type { WorldPoint } from '@/analysis/apiTypes'
import { useStore } from '@/store'
import { STREAM_PULSE_CSS } from '../styles'
import { sevStroke } from '../segments'
import type { HopCluster, HopRole } from '../clusterHops'
import { ClientGlyph, EdgeGlyph, StudioGlyph } from '../glyphs'

interface Props {
  clusters: HopCluster[]
  pulseEpoch: number
  /** Dominant role for glyph shape (clusters should be same-role). */
  role: HopRole
  onHoverEnter: (p: WorldPoint, x: number, y: number) => void
  onHoverLeave: (p: WorldPoint) => void
}

function sevLabel(sev: 0 | 1 | 2 | null): string {
  if (sev === 0) return 'crit'
  if (sev === 1) return 'warn'
  if (sev === 2) return 'ok'
  return '—'
}

function clusterTooltip(c: HopCluster, role: HopRole): WorldPoint {
  const lines: string[] = [
    role === 'studio' ? `Studios · ${c.members.length}`
    : role === 'cdn_edge' ? `CDN edges · ${c.members.length}`
    : `Clients · ${c.members.length}`,
  ]
  const names = c.members.map((m) => m.hop.label || m.hop.value).slice(0, 6)
  lines.push(names.join(', ') + (c.members.length > 6 ? ` +${c.members.length - 6}` : ''))
  lines.push(`streams: ${c.totalStreams}`)
  lines.push(`critical: ${c.criticalCount} · warning: ${c.warningCount} · good: ${c.goodCount}`)
  lines.push(`users: ${c.usersTotal}`)
  lines.push(`worst: ${sevLabel(c.worstSev)}`)

  const worst = c.members.slice().sort((a, b) => (a.sev ?? 2) - (b.sev ?? 2))[0]

  return {
    id: `cluster:${c.id}`,
    label: lines.join('\n'),
    lat: 0,
    lon: 0,
    total: c.totalStreams,
    crit: c.criticalCount,
    warn: c.warningCount,
    sev: (c.worstSev ?? 2) as 0 | 1 | 2,
    usersTotal: c.usersTotal,
    avgSeverity: c.worstSev,
    worstMetric: worst?.hop.worstMetric ?? null,
    metrics: worst?.hop.metrics,
  } as WorldPoint
}

function applyClusterFilters(
  c: HopCluster,
  role: HopRole,
  clientGrain: 'continent' | 'country' | 'city',
  api: {
    setFilterValues: (key: 'studio' | 'cdnEdge' | 'city' | 'country' | 'continent', vals: string[]) => void
    setDimension: (d: 'stream') => void
    setView: (v: 'hex') => void
  },
) {
  if (role === 'studio') {
    const vals = c.members.map((m) => m.hop.value).filter(Boolean)
    api.setFilterValues('studio', vals)
  } else if (role === 'cdn_edge') {
    api.setFilterValues('cdnEdge', c.members.map((m) => m.hop.value).filter(Boolean))
  } else if (clientGrain === 'continent') {
    const continents = c.members.map((m) => m.hop.value).filter(Boolean)
    api.setFilterValues('continent', [...new Set(continents)])
  } else if (clientGrain === 'country') {
    const countries = c.members
      .map((m) => (m.hop.country ?? m.hop.value).toUpperCase())
      .filter(Boolean)
    api.setFilterValues('country', [...new Set(countries)])
  } else {
    const cities: string[] = []
    const countries: string[] = []
    for (const m of c.members) {
      const parts = m.hop.value.split('|')
      if (parts.length === 2) {
        cities.push(parts[0]!)
        if (parts[1]) countries.push(parts[1]!)
      } else if (m.hop.country || /^[A-Za-z]{2}$/.test(m.hop.value)) {
        countries.push(m.hop.country ?? m.hop.value)
      } else {
        cities.push(m.hop.value)
      }
    }
    if (cities.length) api.setFilterValues('city', [...new Set(cities)])
    if (countries.length) api.setFilterValues('country', [...new Set(countries)])
  }
  api.setDimension('stream')
  api.setView('hex')
}

/**
 * Multi-member same-role cluster glyphs with count badge.
 * Click applies filters for all members and opens Hex.
 */
export function ClusterMarkers({
  clusters, pulseEpoch, role, onHoverEnter, onHoverLeave,
}: Props) {
  const setFilterValues = useStore((s) => s.setFilterValues)
  const setDimension = useStore((s) => s.setDimension)
  const setView = useStore((s) => s.setView)
  const clientGrain = useStore((s) => s.worldClientGrain)

  const multi = useMemo(() => clusters.filter((c) => c.members.length > 1), [clusters])

  return (
    <g key={`clusters-${role}-${pulseEpoch}`}>
      <style>{STREAM_PULSE_CSS}</style>
      {multi.map((c) => {
        const { x, y } = c.screenCentroid
        const inactive = c.totalStreams === 0
        const color = inactive ? 'rgba(180,190,210,0.85)' : sevStroke(c.worstSev)
        const point = clusterTooltip(c, role)
        const r = Math.min(22, 12 + Math.sqrt(c.members.length) * 3)
        const fill = (() => {
          if (inactive) return 'rgba(180,190,210,0.85)'
          if (c.worstSev === 0) return 'url(#sev-crit)'
          if (c.worstSev === 1) return 'url(#sev-warn)'
          if (c.worstSev === 2) return 'url(#sev-ok)'
          return 'rgba(180,190,210,0.85)'
        })()

        return (
          <g
            key={c.id}
            style={{ cursor: inactive ? 'default' : 'pointer' }}
            onPointerEnter={() => onHoverEnter(point, x, y)}
            onPointerLeave={() => onHoverLeave(point)}
            onClick={(e) => {
              if (inactive) return
              e.stopPropagation()
              applyClusterFilters(c, role, clientGrain, { setFilterValues, setDimension, setView })
            }}
          >
            {!inactive && (
              <circle className="stream-pulse" cx={x} cy={y} r={r + 4} fill={color} fillOpacity={0.25} />
            )}
            {role === 'studio' && (
              <StudioGlyph cx={x} cy={y} r={r} fill={fill} fillOpacity={inactive ? 0.1 : 0.92} stroke={color} strokeWidth={1.6} />
            )}
            {role === 'city' && (
              <ClientGlyph cx={x} cy={y} r={r} fill={fill} fillOpacity={inactive ? 0.1 : 0.92} stroke={color} strokeWidth={1.6} />
            )}
            {role === 'cdn_edge' && (
              <EdgeGlyph cx={x} cy={y} r={r} fill={fill} fillOpacity={inactive ? 0.15 : 0.9} stroke={color} strokeWidth={1.2} />
            )}
            <text
              x={x}
              y={y + 4}
              textAnchor="middle"
              fill="rgba(255,255,255,0.95)"
              fontSize={11}
              fontWeight={700}
              style={{ pointerEvents: 'none' }}
            >
              {c.members.length}
            </text>
          </g>
        )
      })}
    </g>
  )
}
